'use client';

import React, { ElementType, FC, ReactNode, useRef } from 'react';
import {
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from 'framer-motion';

import { cn } from '@/lib/utils';

interface MovingBorderAnimationProps {
  children: React.ReactNode;
  duration?: number;
  rx?: string;
  ry?: string;
  [key: string]: any;
}

type MovingBorderProps<T extends ElementType = 'div'> = {
  borderRadius?: string;
  children: ReactNode;
  as?: T;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
  rx?: string;
  ry?: string;
} & React.ComponentPropsWithoutRef<T>;

export const MovingBorderAnimation: FC<MovingBorderAnimationProps> = ({ children, duration = 2000, rx, ry, ...otherProps }) => {
  const pathRef = useRef<SVGRectElement>(null);
  const progress = useMotionValue<number>(0);

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength();
    if (length) {
      const pxPerMillisecond = length / duration;
      progress.set((time * pxPerMillisecond) % length);
    }
  });

  const x = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val).x,
  );
  const y = useTransform(
    progress,
    (val) => pathRef.current?.getPointAtLength(val).y,
  );

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        {...otherProps}
      >
        <rect
          fill="none"
          width="100%"
          height="100%"
          rx={rx}
          ry={ry}
          ref={pathRef}
        />
      </svg>
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'inline-block',
          transform,
        }}
      >
        {children}
      </motion.div>
    </>
  );
};

export const MovingBorder = <T extends ElementType = 'div'>({
  borderRadius = '1.5rem',
  children,
  as,
  containerClassName,
  borderClassName,
  duration,
  className,
  rx = '30%',
  ry = '30%',
  ...otherProps
}: MovingBorderProps<T>) => {
  const Component = as || 'div';

  return (
    <Component
      className={cn(
        'bg-transparent relative p-[1px] overflow-hidden ',
        containerClassName,
      )}
      style={{
        borderRadius,
      }}
      {...otherProps}
    >
      <div
        className="absolute inset-0"
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        <MovingBorderAnimation duration={duration} rx={rx} ry={ry}>
          <div
            className={cn(
              'size-20 opacity-[1] bg-[radial-gradient(var(--color-sky-500)_40%,transparent_60%)]',
              borderClassName,
            )}
          />
        </MovingBorderAnimation>
      </div>

      <div
        className={cn(
          'relative border backdrop-blur-xl text-white antialiased',
          className,
        )}
        style={{
          borderRadius: `calc(${borderRadius} * 0.96)`,
        }}
      >
        {children}
      </div>
    </Component>
  );
};
